import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EXPLANATION_REQUIRED,
  FEEDBACK_STATUSES,
  type FeedbackStatus,
  TERMINAL_STATUSES,
  type Suggestion,
  useAssign,
  useChangeStatus,
  useFeedbackModerators,
  useSetAttention,
} from '@/entities/feedback';
import { useViewer } from '@/entities/viewer';
import { useErrorMessage } from '@/shared/i18n';
import { formatDateTime } from '@/shared/lib/datetime';
import { Button, Combobox, HelpRow, TextAreaField } from '@/shared/ui';

import { statusLabel } from './labels';
import styles from './ModerationPanel.module.css';

/** Status change, assignment and the workflow flag. The server re-checks every one of them. */
export function ModerationPanel({ suggestion }: { suggestion: Suggestion }) {
  const { t, i18n } = useTranslation();
  const getMessage = useErrorMessage();
  const viewer = useViewer();
  const me = viewer.status === 'authenticated' ? viewer.user : null;
  const changeStatus = useChangeStatus(suggestion.id);
  const assign = useAssign(suggestion.id);
  const attention = useSetAttention(suggestion.id);
  const moderators = useFeedbackModerators(true);
  const [target, setTarget] = useState<FeedbackStatus>(suggestion.status);
  const [note, setNote] = useState('');

  const reopening = TERMINAL_STATUSES.includes(suggestion.status);
  const needsNote = EXPLANATION_REQUIRED.includes(target) || reopening;
  const unchanged = target === suggestion.status;
  const missingNote = needsNote && note.trim() === '';
  const assigneeId = suggestion.assignee?.id ?? 'none';
  const people = moderators.data ?? [];
  const closed = suggestion.withdrawn;

  return (
    <section className={styles.panel} aria-label={t('feedback.moderation.title')}>
      <HelpRow topic={t('feedback.moderation.title')} topicId="feedback-moderation">
        <h2 className={styles.heading}>{t('feedback.moderation.title')}</h2>
      </HelpRow>

      <div className={styles.block}>
        <Combobox
          label={t('feedback.moderation.status')}
          value={target}
          searchable={false}
          disabled={closed}
          options={FEEDBACK_STATUSES.map((s) => ({ value: s, label: statusLabel(t, s) }))}
          onChange={(value) => {
            setTarget(FEEDBACK_STATUSES.find((s) => s === value) ?? suggestion.status);
          }}
        />
        {!unchanged && (
          <TextAreaField
            label={
              needsNote
                ? t('feedback.moderation.noteRequired')
                : t('feedback.moderation.noteOptional')
            }
            hint={
              reopening && !EXPLANATION_REQUIRED.includes(target)
                ? t('feedback.moderation.reopenHint')
                : t('feedback.moderation.noteHint')
            }
            rows={3}
            maxLength={1000}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        )}
        <Button
          variant="primary"
          disabled={closed || unchanged || missingNote || changeStatus.isPending}
          onClick={() => {
            changeStatus.mutate(
              { status: target, note: note.trim() || null, expected_status: suggestion.status },
              {
                onSuccess: () => {
                  setNote('');
                },
              },
            );
          }}
        >
          {t('feedback.moderation.apply')}
        </Button>
        {changeStatus.isError && (
          <p className={styles.error} role="alert">
            {getMessage(changeStatus.error)}
          </p>
        )}
      </div>

      <div className={styles.block}>
        <Combobox
          label={t('feedback.moderation.assignee')}
          value={assigneeId}
          disabled={assign.isPending}
          options={[
            { value: 'none', label: t('feedback.moderation.unassigned') },
            ...people.map((p) => ({
              value: p.id,
              label:
                p.id === me?.id
                  ? t('feedback.moderation.me', { name: p.display_name })
                  : p.display_name,
            })),
          ]}
          onChange={(value) => {
            assign.mutate(value === 'none' ? null : value);
          }}
        />
        {me && suggestion.assignee?.id !== me.id && (
          <Button
            size="sm"
            disabled={assign.isPending}
            onClick={() => {
              assign.mutate(me.id);
            }}
          >
            {t('feedback.moderation.assignToMe')}
          </Button>
        )}
        {assign.isError && (
          <p className={styles.error} role="alert">
            {getMessage(assign.error)}
          </p>
        )}
      </div>

      <div className={styles.block}>
        <Button
          size="sm"
          disabled={attention.isPending || closed}
          aria-pressed={suggestion.needs_attention === true}
          onClick={() => {
            attention.mutate(!suggestion.needs_attention);
          }}
        >
          {suggestion.needs_attention
            ? t('feedback.moderation.markHandled')
            : t('feedback.moderation.markAttention')}
        </Button>
        <p className={styles.note}>{t('feedback.moderation.attentionNote')}</p>
      </div>

      {suggestion.moderation_events && suggestion.moderation_events.length > 0 && (
        <details className={styles.history}>
          <summary>{t('feedback.moderation.history')}</summary>
          <ol className={styles.events}>
            {suggestion.moderation_events.map((event) => (
              <li key={event.id}>
                <span>
                  {t(`feedback.moderation.event.${event.kind}`, {
                    actor: event.actor?.display_name ?? '—',
                    from: event.from_user?.display_name ?? '—',
                    to: event.to_user?.display_name ?? '—',
                  })}
                </span>
                <time className={styles.when} dateTime={event.created_at}>
                  {formatDateTime(event.created_at, i18n.language, me?.timezone)}
                </time>
              </li>
            ))}
          </ol>
        </details>
      )}
    </section>
  );
}
